import React from "react";
import {
  Rocket, ShieldCheck, AlertTriangle, ShieldX, HelpCircle,
  Clock3, Footprints, Info,
} from "lucide-react";
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from "./ui/card";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";

/**
 * BcvsCard — Bonding Curve Velocity Score (F1, Pump.fun only).
 *
 * Surfaces the BCVS modifier from /api/scan:
 *   report.risk_modifiers.bcvs = {
 *     enabled, ok, is_pumpfun, band: "PASS"|"WARN"|"FAIL"|"skipped",
 *     penalty, skipped_reason,
 *     details: {
 *       bonding_curve_pda, time_to_grad_seconds, time_to_grad_s,
 *       steps_to_grad, steps_truncated, reason,
 *       thresholds: { fast_sec, slow_sec, thin_steps, mid_steps }
 *     }
 *   }
 *
 * UPPERCASE bands. Returns null on undefined prop (never crashes).
 */

function bandMeta(band) {
  switch (band) {
    case "PASS":
      return { label: "Healthy graduation", variant: "success", icon: ShieldCheck };
    case "WARN":
      return { label: "Suspicious velocity", variant: "warning", icon: AlertTriangle };
    case "FAIL":
      return { label: "Insider-completed", variant: "danger", icon: ShieldX };
    default:
      return { label: "Not scored", variant: "muted", icon: HelpCircle };
  }
}

function humanizeSkippedReason(reason) {
  if (!reason) return "";
  if (reason === "disabled") return "BCVS is disabled on the backend.";
  if (reason === "not_pumpfun") return "Not a Pump.fun token — BCVS only scores Pump.fun bonding curves.";
  if (reason === "not_migrated") return "Bonding curve has not graduated yet — BCVS scores migration speed only.";
  if (reason === "no_signatures") return "Helius returned no signatures for the bonding curve PDA.";
  if (reason === "no_block_times") return "Helius signatures had no blockTime values.";
  if (reason === "no_pda_lib") return "solders library missing on the backend; cannot derive PDA.";
  if (reason === "bad_mint") return "Mint address is not a valid Solana pubkey.";
  if (reason.startsWith("rpc_error:")) return `RPC error: ${reason.slice("rpc_error:".length)}`;
  return reason;
}

function formatDuration(seconds) {
  if (seconds == null || Number.isNaN(seconds)) return "—";
  const s = Number(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return rs ? `${m}m ${rs}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h ${rm}m` : `${h}h`;
}

export function BcvsCard({ bcvs }) {
  if (!bcvs) return null;
  const meta = bandMeta(bcvs.band);
  const Icon = meta.icon;
  const d = bcvs.details || {};
  const ttg = d.time_to_grad_s ?? d.time_to_grad_seconds ?? null;
  const steps = d.steps_to_grad ?? null;
  const th = d.thresholds || {};
  const skipped = !bcvs.ok && bcvs.band !== "PASS" && bcvs.band !== "WARN" && bcvs.band !== "FAIL";
  const reasonText = humanizeSkippedReason(bcvs.skipped_reason);

  return (
    <Card className="p-0" data-testid="bcvs-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <Rocket size={16} className="mt-0.5 text-[hsl(var(--muted))]" />
            <div>
              <CardTitle>Bonding Curve Velocity</CardTitle>
              <CardDescription>
                Pump.fun graduation speed — fast + few steps = insider-completed curve.
              </CardDescription>
            </div>
          </div>
          <Badge variant={meta.variant} className="gap-1">
            <Icon size={12} /> {meta.label}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {skipped ? (
          <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))] px-3 py-3">
            <div className="flex items-start gap-2 text-xs text-[hsl(var(--muted))]">
              <Info size={13} className="mt-0.5 shrink-0" />
              <span>{reasonText || "BCVS could not score this token."}</span>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div
                className="px-3 py-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))]"
                data-testid="bcvs-time-to-grad"
              >
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted))]">
                  <Clock3 size={11} /> Time to graduate
                </div>
                <div className="mt-1 font-mono text-base tabular-nums">
                  {formatDuration(ttg)}
                </div>
              </div>
              <div
                className="px-3 py-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))]"
                data-testid="bcvs-steps-to-grad"
              >
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted))]">
                  <Footprints size={11} /> Steps to graduate
                </div>
                <div className="mt-1 font-mono text-base tabular-nums">
                  {steps != null ? steps : "—"}
                  {d.steps_truncated ? (
                    <span className="ml-1 text-[10px] text-[hsl(var(--muted))]">(truncated)</span>
                  ) : null}
                </div>
              </div>
            </div>

            {d.reason ? (
              <p className="text-[11px] text-[hsl(var(--muted))] leading-snug">
                {d.reason}
              </p>
            ) : null}

            {bcvs.penalty ? (
              <>
                <Separator />
                <div className="text-[11px] text-[hsl(var(--muted))]">
                  Score penalty applied:{" "}
                  <span className="font-mono text-[hsl(var(--warning))]">
                    −{bcvs.penalty}
                  </span>
                </div>
              </>
            ) : null}
          </>
        )}

        <p className="text-[10px] text-[hsl(var(--muted))]">
          Thresholds: fast &lt; {th.fast_sec ?? "—"}s · slow &lt; {th.slow_sec ?? "—"}s ·
          thin &lt; {th.thin_steps ?? "—"} steps · mid &lt; {th.mid_steps ?? "—"} steps.
          FAIL = fast AND thin (insider-completed). WARN = fast OR thin.
        </p>
      </CardContent>
    </Card>
  );
}

export default BcvsCard;
